import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminPanel.css';

function getZonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(date);

  const map = {};
  for (const part of parts) {
    if (part.type !== 'literal') {
      map[part.type] = part.value;
    }
  }

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second)
  };
}

function getTimeZoneOffsetMs(date, timeZone) {
  const parts = getZonedParts(date, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return asUtc - date.getTime();
}

/** Wall-clock time in `timeZone` → real UTC Date (DST-safe). */
function zonedWallTimeToUtc(year, month, day, hour, minute, timeZone) {
  let utcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 3; i += 1) {
    const offset = getTimeZoneOffsetMs(new Date(utcMs), timeZone);
    utcMs = Date.UTC(year, month - 1, day, hour, minute, 0) - offset;
  }
  return new Date(utcMs);
}

function addCalendarDays(year, month, day, delta) {
  const utc = new Date(Date.UTC(year, month - 1, day + delta));
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate()
  };
}

/**
 * Publish schedule editor for one game. `scheduleUrl` serves GET/POST
 * { cronExpression, timezone }; `onSaved(text)` runs after a successful save,
 * `onError(text)` after a failed one.
 */
function ScheduleSettings({ scheduleUrl, onSaved, onError }) {
  const [scheduleConfig, setScheduleConfig] = useState({ cronExpression: '0 11 * * *', timezone: 'Europe/Moscow' });
  const [scheduleTime, setScheduleTime] = useState('11:00');
  const [scheduleFrequency, setScheduleFrequency] = useState('daily');
  const [scheduleInterval, setScheduleInterval] = useState(1);
  const [nextWordTime, setNextWordTime] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleFeedback, setScheduleFeedback] = useState(null);

  const calculateNextWordTime = useCallback(() => {
    const timeZone = scheduleConfig.timezone || 'Europe/Moscow';
    const [hour, minute] = scheduleTime.split(':').map(Number);
    const now = new Date();
    const nowParts = getZonedParts(now, timeZone);
    let target;

    if (scheduleFrequency === 'daily') {
      target = zonedWallTimeToUtc(
        nowParts.year,
        nowParts.month,
        nowParts.day,
        hour,
        minute,
        timeZone
      );
      if (now.getTime() >= target.getTime()) {
        const next = addCalendarDays(nowParts.year, nowParts.month, nowParts.day, 1);
        target = zonedWallTimeToUtc(
          next.year,
          next.month,
          next.day,
          hour,
          minute,
          timeZone
        );
      }
    } else if (scheduleFrequency === 'hours') {
      target = zonedWallTimeToUtc(
        nowParts.year,
        nowParts.month,
        nowParts.day,
        hour,
        minute,
        timeZone
      );
      while (target.getTime() <= now.getTime()) {
        target = new Date(target.getTime() + scheduleInterval * 60 * 60 * 1000);
      }
    } else if (scheduleFrequency === 'minutes') {
      target = zonedWallTimeToUtc(
        nowParts.year,
        nowParts.month,
        nowParts.day,
        hour,
        minute,
        timeZone
      );
      while (target.getTime() <= now.getTime()) {
        target = new Date(target.getTime() + scheduleInterval * 60 * 1000);
      }
    } else {
      target = zonedWallTimeToUtc(
        nowParts.year,
        nowParts.month,
        nowParts.day,
        hour,
        minute,
        timeZone
      );
    }

    setNextWordTime(target.toISOString());
  }, [scheduleTime, scheduleConfig.timezone, scheduleFrequency, scheduleInterval]);

  const loadScheduleConfig = useCallback(async () => {
    try {
      const response = await axios.get(scheduleUrl);
      const cronExpr = response.data.cronExpression || '0 11 * * *';
      // Parse cron to extract schedule info
      const parts = cronExpr.split(' ');
      if (parts.length === 5) {
        // Check if it's a daily schedule (minute hour * * *)
        if (parts[2] === '*' && parts[3] === '*' && parts[4] === '*' && !parts[1].includes('/') && parts[1] !== '*') {
          setScheduleFrequency('daily');
          const minute = parts[0] === '*' ? 0 : parseInt(parts[0], 10);
          const hour = parseInt(parts[1], 10);
          setScheduleTime(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
        }
        // Check if it's every X minutes (*/X * * * *)
        else if (parts[0].startsWith('*/') && parts[1] === '*' && parts[2] === '*' && parts[3] === '*' && parts[4] === '*') {
          setScheduleFrequency('minutes');
          const interval = parseInt(parts[0].substring(2), 10);
          setScheduleInterval(interval);
          setScheduleTime('00:00');
        }
        // Check if it's every X hours (minute */X * * *)
        else if (parts[2] === '*' && parts[3] === '*' && parts[4] === '*') {
          setScheduleFrequency('hours');
          const interval = parts[1].startsWith('*/') ? parseInt(parts[1].substring(2), 10) : 1;
          setScheduleInterval(interval);
          const minute = parts[0].includes('*') ? 0 : parseInt(parts[0], 10);
          setScheduleTime(`00:${String(minute).padStart(2, '0')}`);
        }
      }
      setScheduleConfig({
        cronExpression: cronExpr,
        timezone: response.data.timezone || 'Europe/Moscow'
      });
    } catch (error) {
      console.error('Error loading schedule config:', error);
    }
  }, [scheduleUrl]);

  useEffect(() => {
    loadScheduleConfig();
  }, [loadScheduleConfig]);

  useEffect(() => {
    if (scheduleTime && scheduleConfig.timezone) {
      calculateNextWordTime();
    }
  }, [scheduleTime, scheduleConfig.timezone, scheduleFrequency, scheduleInterval, calculateNextWordTime]);

  // Update countdown timer every second
  useEffect(() => {
    if (!nextWordTime) return;

    const updateCountdown = () => {
      const now = new Date();
      const target = new Date(nextWordTime);
      const diff = target.getTime() - now.getTime();

      // If time has passed, recalculate next occurrence
      if (diff <= 0) {
        calculateNextWordTime();
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const parts = [];
      if (hours > 0) parts.push(`${hours}ч`);
      if (minutes > 0 || hours > 0) parts.push(`${minutes}м`);
      parts.push(`${seconds}с`);
      setCountdown(parts.join(' '));
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalId);
  }, [nextWordTime, calculateNextWordTime]);

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    setScheduleSaving(true);
    setScheduleFeedback(null);
    try {
      const [hour, minute] = scheduleTime.split(':').map(Number);
      let cronExpression;

      if (scheduleFrequency === 'daily') {
        // Daily at specified time: minute hour * * *
        cronExpression = `${minute} ${hour} * * *`;
      } else if (scheduleFrequency === 'hours') {
        // Every X hours at specified minute: minute */interval * * *
        // This runs at the specified minute every X hours (e.g., :30 every 2 hours)
        cronExpression = `${minute} */${scheduleInterval} * * *`;
      } else if (scheduleFrequency === 'minutes') {
        // Every X minutes: */interval * * * *
        cronExpression = `*/${scheduleInterval} * * * *`;
      } else {
        cronExpression = `${minute} ${hour} * * *`;
      }

      await axios.post(scheduleUrl, {
        cronExpression,
        timezone: scheduleConfig.timezone
      });

      const timeLabel = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      const successText =
        scheduleFrequency === 'daily'
          ? `Расписание сохранено: каждый день в ${timeLabel} (${scheduleConfig.timezone})`
          : `Расписание сохранено (${scheduleConfig.timezone})`;

      setScheduleConfig({ ...scheduleConfig, cronExpression });
      setScheduleFeedback({ type: 'success', text: successText });
      calculateNextWordTime();
      if (onSaved) await onSaved(successText);

      window.setTimeout(() => {
        document.getElementById('schedule-feedback')?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 50);
    } catch (error) {
      const errorText =
        error.response?.data?.error || 'Ошибка обновления расписания';
      setScheduleFeedback({ type: 'error', text: errorText });
      if (onError) onError(errorText);
    } finally {
      setScheduleSaving(false);
    }
  };

  return (
    <div className="admin-section panel">
      <h2 className="section-title">Расписание публикации</h2>
      <form onSubmit={handleUpdateSchedule} className="schedule-form">
        <label className="admin-label">
          Частота публикации
          <select
            className="field"
            value={scheduleFrequency}
            onChange={(e) => {
              setScheduleFrequency(e.target.value);
              setScheduleFeedback(null);
            }}
          >
            <option value="daily">Каждый день</option>
            <option value="hours">Каждые X часов</option>
            <option value="minutes">Каждые X минут</option>
          </select>
        </label>

        {(scheduleFrequency === 'hours' || scheduleFrequency === 'minutes') && (
          <label className="admin-label">
            Интервал ({scheduleFrequency === 'hours' ? 'часов' : 'минут'})
            <input
              className="field field-narrow"
              type="number"
              min="1"
              max={scheduleFrequency === 'hours' ? '23' : '59'}
              value={scheduleInterval}
              onChange={(e) => setScheduleInterval(parseInt(e.target.value, 10) || 1)}
            />
          </label>
        )}

        <div className="admin-label">
          <span>Время начала</span>
          <div className="time-row">
            <label className="time-part">
              <span className="time-part-label">Час</span>
              <select
                className="field"
                value={(scheduleTime || '11:00').split(':')[0] || '11'}
                onChange={(e) => {
                  const minute = (scheduleTime || '11:00').split(':')[1] || '00';
                  setScheduleTime(`${e.target.value}:${minute}`);
                  setScheduleFeedback(null);
                }}
              >
                {Array.from({ length: 24 }, (_, hour) => {
                  const value = String(hour).padStart(2, '0');
                  return (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  );
                })}
              </select>
            </label>
            <span className="time-separator" aria-hidden="true">:</span>
            <label className="time-part">
              <span className="time-part-label">Мин</span>
              <select
                className="field"
                value={(scheduleTime || '11:00').split(':')[1] || '00'}
                onChange={(e) => {
                  const hour = (scheduleTime || '11:00').split(':')[0] || '11';
                  setScheduleTime(`${hour}:${e.target.value}`);
                  setScheduleFeedback(null);
                }}
              >
                {Array.from({ length: 60 }, (_, minute) => {
                  const value = String(minute).padStart(2, '0');
                  return (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>
          <span className="meta-line">
            {scheduleFrequency === 'daily'
              ? 'Публикация каждый день в это время'
              : scheduleFrequency === 'hours'
                ? `Каждые ${scheduleInterval} ч., начиная с этого времени`
                : `Каждые ${scheduleInterval} мин., начиная с этого времени`}
          </span>
        </div>

        <label className="admin-label">
          Часовой пояс
          <select
            className="field"
            value={scheduleConfig.timezone}
            onChange={(e) => {
              setScheduleConfig({ ...scheduleConfig, timezone: e.target.value });
              setScheduleFeedback(null);
            }}
          >
            <option value="Europe/Moscow">Europe/Moscow (МСК)</option>
            <option value="Europe/Kiev">Europe/Kiev (Киев)</option>
            <option value="Europe/Minsk">Europe/Minsk (Минск)</option>
            <option value="UTC">UTC</option>
            <option value="America/New_York">America/New_York (EST)</option>
            <option value="Europe/London">Europe/London (GMT)</option>
            <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
          </select>
        </label>

        {nextWordTime && (
          <div
            className={`next-publish panel${
              scheduleFeedback?.type === 'success' ? ' next-publish--flash' : ''
            }`}
          >
            <p>
              <strong>Следующая публикация</strong>
            </p>
            <p>
              {new Date(nextWordTime).toLocaleString('ru-RU', {
                timeZone: scheduleConfig.timezone,
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short'
              })}
            </p>
            {countdown && (
              <p className="countdown-line">Через {countdown}</p>
            )}
          </div>
        )}

        {scheduleFeedback && (
          <div
            id="schedule-feedback"
            className={`alert ${
              scheduleFeedback.type === 'error' ? 'alert-error' : 'alert-success'
            } schedule-feedback`}
            role="status"
          >
            {scheduleFeedback.text}
          </div>
        )}

        <button
          type="submit"
          className={`btn btn-primary${
            scheduleFeedback?.type === 'success' && !scheduleSaving
              ? ' btn-schedule-saved'
              : ''
          }`}
          disabled={scheduleSaving}
        >
          {scheduleSaving
            ? 'Сохраняю…'
            : scheduleFeedback?.type === 'success'
              ? 'Сохранено ✓'
              : 'Сохранить расписание'}
        </button>
      </form>
    </div>
  );
}

export default ScheduleSettings;
